/**
 * SVG path command parser — minimal subset that covers ~95% of real-world icons:
 *
 *   M x y            moveto absolute
 *   m dx dy          moveto relative
 *   L x y            lineto absolute
 *   l dx dy          lineto relative
 *   H x              horizontal lineto absolute
 *   h dx             horizontal lineto relative
 *   V y              vertical lineto absolute
 *   v dy             vertical lineto relative
 *   C x1 y1 x2 y2 x y  cubic bezier absolute
 *   c dx1 dy1 ...      cubic bezier relative
 *   Q x1 y1 x y        quadratic bezier absolute
 *   q dx1 dy1 ...      quadratic bezier relative
 *   Z / z            closepath
 *
 * (S, T, A skipped — smooth cubic/quadratic shorthands and elliptical arc
 *  are common but not needed for the initial icon set.)
 *
 * Coordinates are in the SVG's own user-space (caller passes a viewBox
 * extents → canvas-size scale factor at render time).
 */

export interface SvgSubPath {
  /** Subpath starting point. */
  startX: number;
  startY: number;
  /** Path commands. Coordinates in user-space. */
  cmds: SvgCmd[];
}

export type SvgCmd =
  | { kind: "M"; x: number; y: number }
  | { kind: "L"; x: number; y: number }
  | { kind: "C"; x1: number; y1: number; x2: number; y2: number; x: number; y: number }
  | { kind: "Q"; x1: number; y1: number; x: number; y: number }
  | { kind: "Z" };

/**
 * Parse an SVG path `d` attribute into a sequence of subpaths.
 * Returns [] for invalid input — caller falls back to a bounding-box rect.
 */
export function parseSvgPath(d: string): SvgSubPath[] {
  if (!d) return [];
  const out: SvgSubPath[] = [];
  const tokens = tokenize(d);
  let i = 0;
  let cx = 0, cy = 0;        // current point
  let sx = 0, sy = 0;        // subpath start
  let cur: SvgSubPath | null = null;
  let lastCmd = "";

  while (i < tokens.length) {
    let t = tokens[i];
    let cmd: string;
    if (typeof t === "string") {
      cmd = t;
      i++;
    } else {
      // Implicit repeat: a number following M/m/L/l/T/t continues as line-tos,
      // following H/h/V/v repeats as itself, etc. We simplify: any number
      // after M/m continues as L/l (per SVG spec).
      cmd = lastCmd === "M" ? "L" :
            lastCmd === "m" ? "l" :
            lastCmd;
    }

    if (cmd === "M" || cmd === "m") {
      const x = num(tokens[i++]); const y = num(tokens[i++]);
      const isRel = cmd === "m";
      const tx = isRel ? cx + x : x;
      const ty = isRel ? cy + y : y;
      cx = tx; cy = ty; sx = tx; sy = ty;
      cur = { startX: tx, startY: ty, cmds: [{ kind: "M", x: tx, y: ty }] };
      out.push(cur);
      lastCmd = cmd;
    } else if (cmd === "L" || cmd === "l") {
      const x = num(tokens[i++]); const y = num(tokens[i++]);
      const isRel = cmd === "l";
      const tx = isRel ? cx + x : x;
      const ty = isRel ? cy + y : y;
      cx = tx; cy = ty;
      cur?.cmds.push({ kind: "L", x: tx, y: ty });
      lastCmd = cmd;
    } else if (cmd === "H" || cmd === "h") {
      const x = num(tokens[i++]);
      const isRel = cmd === "h";
      const tx = isRel ? cx + x : x;
      cx = tx;
      cur?.cmds.push({ kind: "L", x: tx, y: cy });
      lastCmd = cmd;
    } else if (cmd === "V" || cmd === "v") {
      const y = num(tokens[i++]);
      const isRel = cmd === "v";
      const ty = isRel ? cy + y : y;
      cy = ty;
      cur?.cmds.push({ kind: "L", x: cx, y: ty });
      lastCmd = cmd;
    } else if (cmd === "C" || cmd === "c") {
      const x1 = num(tokens[i++]); const y1 = num(tokens[i++]);
      const x2 = num(tokens[i++]); const y2 = num(tokens[i++]);
      const x = num(tokens[i++]);  const y = num(tokens[i++]);
      const isRel = cmd === "c";
      const ax1 = isRel ? cx + x1 : x1, ay1 = isRel ? cy + y1 : y1;
      const ax2 = isRel ? cx + x2 : x2, ay2 = isRel ? cy + y2 : y2;
      const ax = isRel ? cx + x : x, ay = isRel ? cy + y : y;
      cx = ax; cy = ay;
      cur?.cmds.push({ kind: "C", x1: ax1, y1: ay1, x2: ax2, y2: ay2, x: ax, y: ay });
      lastCmd = cmd;
    } else if (cmd === "Q" || cmd === "q") {
      const x1 = num(tokens[i++]); const y1 = num(tokens[i++]);
      const x = num(tokens[i++]);  const y = num(tokens[i++]);
      const isRel = cmd === "q";
      const ax1 = isRel ? cx + x1 : x1, ay1 = isRel ? cy + y1 : y1;
      const ax = isRel ? cx + x : x, ay = isRel ? cy + y : y;
      cx = ax; cy = ay;
      cur?.cmds.push({ kind: "Q", x1: ax1, y1: ay1, x: ax, y: ay });
      lastCmd = cmd;
    } else if (cmd === "Z" || cmd === "z") {
      cur?.cmds.push({ kind: "Z" });
      if (cur) { cx = cur.startX; cy = cur.startY; }
      lastCmd = cmd;
    } else if (cmd === "S" || cmd === "s" || cmd === "T" || cmd === "t" || cmd === "A" || cmd === "a") {
      // Not supported in v1. Skip args: S/s = 4, T/t = 2, A/a = 7.
      const argc = (cmd === "S" || cmd === "s") ? 4
                  : (cmd === "T" || cmd === "t") ? 2
                  : 7;
      for (let k = 0; k < argc; k++) i++;
      lastCmd = cmd;
    } else {
      // Unknown command token — skip the letter.
      if (typeof t === "string") i++;
    }
  }
  return out;
}

function num(t: string | number | undefined): number {
  if (t === undefined) return 0;
  const v = typeof t === "number" ? t : Number(t);
  return Number.isFinite(v) ? v : 0;
}

function tokenize(d: string): Array<string | number> {
  const out: Array<string | number> = [];
  // Match a letter (command) OR a floating-point number.
  // We keep numbers as JS numbers and letters as strings.
  const re = /([MmLlHhVvCcQqZzSsTtAa])|(-?\d*\.?\d+(?:[eE][-+]?\d+)?)/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(d)) !== null) {
    if (m[1]) out.push(m[1]);
    else if (m[2]) out.push(Number(m[2]));
  }
  return out;
}

/**
 * Rasterize parsed subpaths to a canvas. Returns the canvas and its px size.
 * Caller is responsible for uploading to a GL texture.
 */
export function bakePathToCanvas(
  d: string,
  cssW: number,
  cssH: number,
  fillColor: { r: number; g: number; b: number; a: number },
  viewBox?: { x: number; y: number; w: number; h: number },
  dpr: number = Math.min((typeof window !== "undefined" ? window.devicePixelRatio : 1) || 1, 2.0),
): HTMLCanvasElement {
  const subs = parseSvgPath(d);
  const W = Math.max(2, Math.ceil(cssW * dpr));
  const H = Math.max(2, Math.ceil(cssH * dpr));
  const canvas = document.createElement("canvas");
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext("2d")!;

  // User-space → canvas-space scale.
  // viewBox defaults to "0 0 <cssW> <cssH>" if not given.
  const vb = viewBox ?? { x: 0, y: 0, w: cssW, h: cssH };
  const sx = (W / vb.w);
  const sy = (H / vb.h);

  ctx.fillStyle = `rgba(${Math.round(fillColor.r * 255)}, ${Math.round(fillColor.g * 255)}, ${Math.round(fillColor.b * 255)}, ${Math.round(fillColor.a * 255)})`;
  ctx.beginPath();

  for (const sub of subs) {
    for (const c of sub.cmds) {
      if (c.kind === "M") {
        ctx.moveTo((c.x - vb.x) * sx, (c.y - vb.y) * sy);
      } else if (c.kind === "L") {
        ctx.lineTo((c.x - vb.x) * sx, (c.y - vb.y) * sy);
      } else if (c.kind === "C") {
        ctx.bezierCurveTo(
          (c.x1 - vb.x) * sx, (c.y1 - vb.y) * sy,
          (c.x2 - vb.x) * sx, (c.y2 - vb.y) * sy,
          (c.x - vb.x)  * sx, (c.y - vb.y)  * sy,
        );
      } else if (c.kind === "Q") {
        ctx.quadraticCurveTo(
          (c.x1 - vb.x) * sx, (c.y1 - vb.y) * sy,
          (c.x - vb.x)  * sx, (c.y - vb.y)  * sy,
        );
      } else if (c.kind === "Z") {
        ctx.closePath();
      }
    }
  }
  ctx.fill("nonzero");
  return canvas;
}
